import axios from "axios"; // tslint:disable-line match-default-export-name

import { gGateway } from "@utils/gateway";

import { nonConcurrent } from "@use/decorators";
import { EntityStore } from "@use/entity-store";
import { Collection } from "@use/models";

import { api } from "@courses-models/institution/src/ts/config";

/**
 * Describes data returned by v1/Unic/Institution/Grades/{institutionNumber}
 * from Gyldendal Access Service.
 */
type v1_Unic_Institution_Grades_Response = {
  Name: string;
  Classes: { Name: string }[];
}[];

/**
 * Specification of the data format available within this collection.
 */
type Grade = {
  id: string;
  name: string;
};

/**
 * Memory cache of collections that have already been fetched by the client, so
 * that we don't need to fetch the same collections over and over again.
 */
const cache = new EntityStore<InstitutionGradeCollection>({ ttl: 600 });

/**
 * Iterable collection of grades available on the given institution.
 */
export class InstitutionGradeCollection extends Collection<Grade> {
  /**
   * Specifies the id of the institution that this collection is associated
   * with.
   */
  public readonly institutionId: string;

  /**
   * Contains an internal set containing all items available within this
   * collection.
   */
  protected readonly collection: Grade[];

  /**
   * Cache collection of items when this collection is instantiated.
   */
  constructor(initial: { collection: Grade[]; institutionId: string }) {
    super();

    this.collection = initial.collection;
    this.institutionId = initial.institutionId;
  }

  /**
   * Fetches a collection of courses that was shared with this student, and
   * converts each course into a well-typed model.
   */
  @nonConcurrent()
  public static async GET_BY_ID(
    institutionId: string
  ): Promise<InstitutionGradeCollection> {
    // If this collection has already been cached, then return an instance
    // of this method directly from there
    if (cache.has(institutionId)) {
      return <InstitutionGradeCollection>cache.get(institutionId);
    }

    // Fetch data about the user from Gyldendal Access Service
    const endpoint = `${api.gyldendalAccessService(
      "v1"
    )}/api/v1/Unic/Institution/Grades/${encodeURIComponent(institutionId)}`;

    const response = await axios.get<v1_Unic_Institution_Grades_Response>(
      gGateway(endpoint)
    );

    // Format data into an array of class objects
    const models = response.data.map((item) => ({
      id: item.Name,
      name: item.Name,
    }));

    // Create our collection
    const collection = new InstitutionGradeCollection({
      collection: models,
      institutionId,
    });

    // Cache the collection for future use
    cache.set(institutionId, collection);

    // Return the collection publicly
    return collection;
  }
}
